import React, { useState } from 'react';
import { submitDhabaRegistration } from '../services/registrationService';

const TAG_OPTIONS = [
  'Famous Parathas', 'Legendary', 'Family Restaurant', 'Celebrity Owned',
  'Truck Friendly', 'Highway Classic', 'Local Cuisine', 'Premium Dining'
];

const PHOTO_CATEGORIES = [
  { id: 'exterior', label: 'Exterior / Entrance', required: true, helper: 'Front view, signboard, entrance from highway' },
  { id: 'parking', label: 'Parking Area', required: false, helper: 'Parking space for cars/trucks' },
  { id: 'dining', label: 'Dining Area', required: true, helper: 'Tables, seating, serving area' },
  { id: 'kitchen', label: 'Kitchen / Cooking Area', required: true, helper: 'Show cooking surfaces, utensils, stove area' },
  { id: 'food_storage', label: 'Food Display / Storage', required: false, helper: 'Fridge, storage, food display counter' },
  { id: 'menu_board', label: 'Menu Board', required: false, helper: 'Price list, menu display board' },
  { id: 'washroom', label: 'Washroom', required: true, helper: 'Washbasin, toilet area, cleanliness' },
];

export function DhabaRegistrationForm({ onBack, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: '',
    description: '',
    dhaba_type: 'dhaba',
    highway_type: 'NH',
    highway_number: '',
    highway: '',
    highway_km: '',
    address: '',
    
    // Step 2: Location
    latitude: '',
    longitude: '',
    state: '',
    district: '',
    // Step 3: Cuisine & Timings
    cuisine: [],
    price_range: '₹₹',
    open_24x7: false,
    open_time: '06:00',
    close_time: '23:00',
    pure_veg: false,
    tags: [],
    
    // Step 4: Amenities
    parking: false,
    parking_type: [],
    washroom: false,
    ac_dining: false,
    wifi: false,
    card_accepted: false,
    family_friendly: false,
    play_area: false,
    ev_charging: false,
    
    // Step 5: Owner Details
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    registered_address: '',
    gstn: '',
    fssai_number: '',
    contact_phone: '',
    
    // Step 6: Photos (categorized)
    photos: {
      kitchen: [],
      dining: [],
      food_storage: [],
      washroom: [],
      exterior: [],
      parking: [],
      menu_board: [],
    },
    images: [] // legacy flat array
  });
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific error when user edits
    if (fieldErrors[field]) {
      setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
  };
  
  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  // Validation helpers
  const isValidPhone = (p) => /^[6-9]\d{9}$/.test((p || '').replace(/\D/g, '').slice(-10));
  const isValidEmail = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const isValidGSTN = (g) => !g || /^\d{2}[A-Z0-9]{13}$/.test(g.toUpperCase());
  const isValidLat = (v) => { const n = parseFloat(v); return !isNaN(n) && n >= 6.0 && n <= 37.0; };
  const isValidLng = (v) => { const n = parseFloat(v); return !isNaN(n) && n >= 68.0 && n <= 97.5; };

  const validateStep = (s) => {
    const errors = {};
    if (s === 1) {
      if (!formData.name.trim()) errors.name = 'Dhaba name is required';
      if (!formData.highway_number.trim()) {
        if (formData.highway_type === 'EXP') errors.highway_number = 'Expressway name is required';
        else errors.highway_number = 'Highway number is required';
      }
      if (!formData.address.trim()) errors.address = 'Address is required';
      if (formData.highway_km && (isNaN(parseFloat(formData.highway_km)) || parseFloat(formData.highway_km) < 0)) {
        errors.highway_km = 'Must be a positive number';
      }
    }
    if (s === 2) {
      if (!formData.latitude) errors.latitude = 'Latitude is required';
      else if (!isValidLat(formData.latitude)) errors.latitude = 'Must be between 6.0 and 37.0 (India)';
      if (!formData.longitude) errors.longitude = 'Longitude is required';
      else if (!isValidLng(formData.longitude)) errors.longitude = 'Must be between 68.0 and 97.5 (India)';
    }
    if (s === 3) {
      if (formData.cuisine.length === 0) errors.cuisine = 'Select at least one cuisine';
      if (!formData.open_24x7 && formData.open_time && formData.close_time) {
        if (formData.close_time <= formData.open_time) errors.close_time = 'Close time must be after open time';
      }
    }
    if (s === 5) {
      if (!formData.owner_name.trim()) errors.owner_name = 'Owner name is required';
      if (!formData.owner_phone) errors.owner_phone = 'Owner phone is required';
      else if (!isValidPhone(formData.owner_phone)) errors.owner_phone = 'Enter valid 10-digit mobile (starts with 6-9)';
      if (formData.owner_email && !isValidEmail(formData.owner_email)) errors.owner_email = 'Enter valid email address';
      if (formData.gstn && !isValidGSTN(formData.gstn)) errors.gstn = 'Enter valid 15-char GSTN (e.g., 22AAAAA0000A1Z5)';
      if (formData.fssai_number && !/^\d{14}$/.test(formData.fssai_number)) errors.fssai_number = 'FSSAI must be exactly 14 digits';
      if (formData.contact_phone && !isValidPhone(formData.contact_phone)) errors.contact_phone = 'Enter valid 10-digit mobile';
    }
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(step);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setStep(prev => prev + 1);
  };

  const FieldError = ({ field }) => {
    if (!fieldErrors[field]) return null;
    return <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>{fieldErrors[field]}</p>;
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Build standardized highway string
      let highway = formData.highway_type === 'EXP'
        ? formData.highway_number.trim()
        : `${formData.highway_type}-${formData.highway_number.trim()}`;

      const submitData = {
        ...formData,
        highway,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        highway_km: formData.highway_km ? parseFloat(formData.highway_km) : null
      };
      
      const result = await submitDhabaRegistration(submitData);
      onSuccess?.(result);
    } catch (err) {
      setError(err.message || 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };
  
  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.name && formData.highway_number && formData.address;
      case 2:
        return formData.latitude && formData.longitude;
      case 3:
        return formData.cuisine.length > 0;
      case 4:
        return true; // Amenities are optional
      case 5:
        return formData.owner_name && formData.owner_phone;
      case 6:
        return true; // Photos optional for now
      default:
        return false;
    }
  };
  
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3, 4, 5, 6].map(s => (
        <div
          key={s}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '600',
            background: s === step 
              ? 'linear-gradient(135deg, #D4AF37, #C5A028)' 
              : s < step 
                ? '#10B981' 
                : '#E5E7EB',
            color: s <= step ? (s === step ? '#121212' : '#FFFFFF') : '#6B7280',
            boxShadow: s === step ? '0 4px 12px rgba(212, 175, 55, 0.4)' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          {s < step ? '✓' : s}
        </div>
      ))}
    </div>
  );
  
  const renderStep1 = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Basic Information</h2>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Dhaba/Restaurant Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => updateField('name', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="Enter dhaba name"
          data-testid="dhaba-name-input"
          style={fieldErrors.name ? { borderColor: '#EF4444' } : {}}
        />
        <FieldError field="name" />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
        <select
          value={formData.dhaba_type}
          onChange={(e) => updateField('dhaba_type', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
        >
          <option value="dhaba">Dhaba</option>
          <option value="restaurant">Restaurant</option>
          <option value="food_court">Food Court</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Highway *</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={formData.highway_type}
            onChange={(e) => updateField('highway_type', e.target.value)}
            style={{ width: '130px', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '12px', outline: 'none', fontSize: '14px', background: '#FAFAFA' }}
            data-testid="highway-type-select"
          >
            <option value="NH">NH (National)</option>
            <option value="SH">SH (State)</option>
            <option value="EXP">Expressway</option>
          </select>
          <input
            type={formData.highway_type === 'EXP' ? 'text' : 'text'}
            value={formData.highway_number}
            onChange={(e) => {
              const val = formData.highway_type === 'EXP' ? e.target.value : e.target.value.replace(/[^0-9A-Za-z]/g, '');
              updateField('highway_number', val);
              // Auto-update the combined highway field for display
              const hw = formData.highway_type === 'EXP' ? val : `${formData.highway_type}-${val}`;
              updateField('highway', hw);
            }}
            placeholder={formData.highway_type === 'EXP' ? 'e.g., Yamuna Expressway' : 'e.g., 48'}
            style={{ flex: 1, padding: '12px', border: fieldErrors.highway_number ? '2px solid #EF4444' : '2px solid #E5E7EB', borderRadius: '12px', outline: 'none', fontSize: '14px' }}
            data-testid="highway-number-input"
          />
        </div>
        <FieldError field="highway_number" />
        <FieldError field="highway" />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Highway KM Marker</label>
        <input
          type="number"
          value={formData.highway_km}
          onChange={(e) => updateField('highway_km', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="e.g., 125"
        />
        <FieldError field="highway_km" />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Address *</label>
        <textarea
          value={formData.address}
          onChange={(e) => updateField('address', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="Complete address"
          rows={3}
          data-testid="address-input"
          style={fieldErrors.address ? { borderColor: '#EF4444' } : {}}
        />
        <FieldError field="address" />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="Brief description about your dhaba"
          rows={2}
        />
      </div>
    </div>
  );
  
  const renderStep2 = () => {
    // Helper: reverse-geocode a coordinate to fill address, state, district
    const reverseGeocode = async (latitude, longitude) => {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
        );
        const data = await response.json();
        if (data.results && data.results[0]) {
          const result = data.results[0];
          // Extract state (administrative_area_level_1) and district (administrative_area_level_2 or _3)
          let stateName = '';
          let districtName = '';
          for (const comp of (result.address_components || [])) {
            if (comp.types.includes('administrative_area_level_1')) {
              stateName = comp.long_name;
            }
            if (comp.types.includes('administrative_area_level_2') && !districtName) {
              districtName = comp.long_name.replace(/\s*District$/i, '');
            } else if (comp.types.includes('administrative_area_level_3') && !districtName) {
              districtName = comp.long_name;
            }
          }
          setFormData(prev => ({
            ...prev,
            address: prev.address || result.formatted_address,
            state: stateName || prev.state,
            district: districtName || prev.district,
          }));
        }
      } catch (err) {
        console.log('Could not fetch address:', err);
      }
    };

    const getCurrentLocation = () => {
      setLocationLoading(true);
      setLocationError('');
      
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by your browser. Please enter coordinates manually.');
        setLocationLoading(false);
        return;
      }
      
      // Show info that we're requesting permission
      console.log('Requesting location permission...');
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          console.log('Location received:', position.coords);
          const { latitude, longitude } = position.coords;
          updateField('latitude', latitude.toFixed(6));
          updateField('longitude', longitude.toFixed(6));
          
          await reverseGeocode(latitude, longitude);
          
          setLocationLoading(false);
          setLocationError('');
        },
        (error) => {
          console.log('Location error:', error);
          let errorMsg = 'Unable to get your location. ';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = '⚠️ Location permission was denied. Please allow location access in your browser settings and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = '⚠️ Location information is unavailable. Please check if GPS/Location is enabled on your device.';
              break;
            case error.TIMEOUT:
              errorMsg = '⚠️ Location request timed out. Please try again or enter coordinates manually.';
              break;
            default:
              errorMsg = '⚠️ Could not detect location. Please enter coordinates manually.';
          }
          setLocationError(errorMsg);
          setLocationLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        }
      );
    };

    return (
      <div className="space-y-4">
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1A1A1A' }}>Location</h2>
        <p style={{ color: '#6B7280', fontSize: '14px' }}>We'll auto-detect your location or you can enter manually</p>
        
        {/* Info box about location permission */}
        {!formData.latitude && !locationLoading && !locationError && (
          <div style={{
            padding: '12px 16px',
            background: '#FFFBEB',
            border: '1px solid #FCD34D',
            borderRadius: '12px',
            fontSize: '13px',
            color: '#92400E'
          }}>
            💡 <strong>Tip:</strong> When you click the button below, your browser will ask for location permission. Please click "Allow" to auto-detect your coordinates.
          </div>
        )}
        
        {/* Auto-detect Location Button */}
        <button
          type="button"
          onClick={getCurrentLocation}
          disabled={locationLoading}
          style={{
            width: '100%',
            padding: '18px',
            borderRadius: '12px',
            border: locationLoading ? '2px solid #D4AF37' : '2px dashed #D4AF37',
            background: locationLoading ? 'rgba(212, 175, 55, 0.15)' : 'rgba(212, 175, 55, 0.05)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            cursor: locationLoading ? 'wait' : 'pointer',
            transition: 'all 0.2s ease'
          }}
          data-testid="detect-location-btn"
        >
          {locationLoading ? (
            <>
              <div style={{
                width: '22px',
                height: '22px',
                border: '3px solid #D4AF37',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{ textAlign: 'left' }}>
                <span style={{ color: '#D4AF37', fontWeight: '600', display: 'block' }}>Detecting Location...</span>
                <span style={{ color: '#92700C', fontSize: '12px' }}>Please allow location access if prompted</span>
              </div>
            </>
          ) : (
            <>
              <span style={{ fontSize: '28px' }}>📍</span>
              <div style={{ textAlign: 'left' }}>
                <span style={{ color: '#D4AF37', fontWeight: '700', display: 'block', fontSize: '15px' }}>Use My Current Location</span>
                <span style={{ color: '#92700C', fontSize: '12px' }}>Tap to auto-detect GPS coordinates</span>
              </div>
            </>
          )}
        </button>
        
        {locationError && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            color: '#EF4444',
            fontSize: '14px'
          }}>
            {locationError}
          </div>
        )}
        
        {(formData.latitude && formData.longitude) && (
          <div style={{
            padding: '16px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '18px' }}>✅</span>
              <span style={{ color: '#059669', fontWeight: '600', fontSize: '14px' }}>Location Detected</span>
            </div>
            <p style={{ color: '#065F46', fontSize: '13px' }}>
              Lat: {formData.latitude}, Lng: {formData.longitude}
            </p>
            {(formData.state || formData.district) && (
              <p style={{ color: '#065F46', fontSize: '13px', marginTop: '4px' }}>
                {formData.district && <>📍 {formData.district}</>}{formData.district && formData.state && ', '}{formData.state}
              </p>
            )}
          </div>
        )}
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px',
          margin: '20px 0'
        }}>
          <div style={{ flex: 1, height: '1px', background: '#E5E7EB' }} />
          <span style={{ color: '#9CA3AF', fontSize: '12px', fontWeight: '500' }}>OR ENTER MANUALLY</span>
          <div style={{ flex: 1, height: '1px', background: '#E5E7EB' }} />
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              Latitude *
            </label>
            <input
              type="number"
              step="any"
              value={formData.latitude}
              onChange={(e) => updateField('latitude', e.target.value)}
              onBlur={() => {
                const lat = parseFloat(formData.latitude);
                const lng = parseFloat(formData.longitude);
                if (!isNaN(lat) && !isNaN(lng)) reverseGeocode(lat, lng);
              }}
              placeholder="28.6139"
              data-testid="latitude-input"
              style={{
                width: '100%', padding: '14px 16px',
                border: fieldErrors.latitude ? '2px solid #EF4444' : '2px solid #E5E7EB',
                borderRadius: '12px', outline: 'none', fontSize: '15px', background: '#FAFAFA'
              }}
            />
            <FieldError field="latitude" />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              Longitude *
            </label>
            <input
              type="number"
              step="any"
              value={formData.longitude}
              onChange={(e) => updateField('longitude', e.target.value)}
              onBlur={() => {
                const lat = parseFloat(formData.latitude);
                const lng = parseFloat(formData.longitude);
                if (!isNaN(lat) && !isNaN(lng)) reverseGeocode(lat, lng);
              }}
              placeholder="77.2090"
              data-testid="longitude-input"
              style={{
                width: '100%', padding: '14px 16px',
                border: fieldErrors.longitude ? '2px solid #EF4444' : '2px solid #E5E7EB',
                borderRadius: '12px', outline: 'none', fontSize: '15px', background: '#FAFAFA'
              }}
            />
            <FieldError field="longitude" />
          </div>
        </div>

        {/* State + District (auto-detected, editable) */}
        {(formData.latitude && formData.longitude) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                State *
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => updateField('state', e.target.value)}
                placeholder="Auto-detected from location"
                data-testid="state-input"
                style={{
                  width: '100%', padding: '14px 16px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px', outline: 'none', fontSize: '15px', background: '#FAFAFA'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                District
              </label>
              <input
                type="text"
                value={formData.district}
                onChange={(e) => updateField('district', e.target.value)}
                placeholder="Auto-detected"
                data-testid="district-input"
                style={{
                  width: '100%', padding: '14px 16px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px', outline: 'none', fontSize: '15px', background: '#FAFAFA'
                }}
              />
            </div>
          </div>
        )}
        
        <div style={{
          padding: '16px',
          background: '#F0F9FF',
          borderRadius: '12px',
          marginTop: '16px'
        }}>
          <p style={{ fontWeight: '600', color: '#0369A1', fontSize: '14px', marginBottom: '8px' }}>
            💡 How to get coordinates manually:
          </p>
          <ol style={{ color: '#0C4A6E', fontSize: '13px', marginLeft: '16px', lineHeight: '1.6' }}>
            <li>Open Google Maps on your phone</li>
            <li>Long-press on your dhaba location</li>
            <li>Copy the coordinates shown at the bottom</li>
          </ol>
        </div>
      </div>
    );
  };
  
  // Cuisine options with images
  const CUISINE_WITH_IMAGES = [
    { name: 'North Indian', image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=300&h=200&fit=crop', emoji: '🍛' },
    { name: 'South Indian', image: 'https://images.unsplash.com/photo-1630383249896-424e482df921?w=300&h=200&fit=crop', emoji: '🥘' },
    { name: 'Punjabi', image: 'https://images.unsplash.com/photo-1672477179695-7276b0602fa9?w=300&h=200&fit=crop', emoji: '🫓' },
    { name: 'Rajasthani', image: 'https://images.unsplash.com/photo-1586981114766-708f09a71e20?w=300&h=200&fit=crop', emoji: '🏜️' },
    { name: 'Gujarati', image: 'https://images.unsplash.com/photo-1567337710282-00832b415979?w=300&h=200&fit=crop', emoji: '🥗' },
    { name: 'Mughlai', image: 'https://images.unsplash.com/photo-1589302168068-964664d93dc0?w=300&h=200&fit=crop', emoji: '👑' },
    { name: 'Chinese', image: 'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?w=300&h=200&fit=crop', emoji: '🥢' },
    { name: 'Continental', image: 'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?w=300&h=200&fit=crop', emoji: '🍝' },
    { name: 'Street Food', image: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?w=300&h=200&fit=crop', emoji: '🍟' },
    { name: 'Sweets', image: 'https://images.unsplash.com/photo-1548848221-0c2e497ed557?w=300&h=200&fit=crop', emoji: '🍬' },
  ];

  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1A1A1A', marginBottom: '4px' }}>Cuisine & Timings</h2>
        <p style={{ color: '#6B7280', fontSize: '14px' }}>Tell us about your food and operating hours</p>
      </div>
      
      {/* Cuisine Types with Images */}
      <div>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '600', 
          color: '#374151', 
          marginBottom: '12px' 
        }}>
          Cuisine Types * <span style={{ fontWeight: '400', color: '#9CA3AF' }}>(Select all that apply)</span>
        </label>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '12px'
        }}>
          {CUISINE_WITH_IMAGES.map(cuisine => (
            <button
              key={cuisine.name}
              type="button"
              onClick={() => toggleArrayItem('cuisine', cuisine.name)}
              style={{
                padding: '0',
                borderRadius: '16px',
                border: formData.cuisine.includes(cuisine.name) ? '3px solid #D4AF37' : '2px solid #E5E7EB',
                background: '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                overflow: 'hidden',
                boxShadow: formData.cuisine.includes(cuisine.name) 
                  ? '0 4px 12px rgba(212, 175, 55, 0.3)' 
                  : '0 2px 6px rgba(0,0,0,0.05)',
                transform: formData.cuisine.includes(cuisine.name) ? 'scale(1.02)' : 'scale(1)',
                position: 'relative'
              }}
            >
              {/* Selected checkmark */}
              {formData.cuisine.includes(cuisine.name) && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #D4AF37 0%, #C5A028 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1A1A1A',
                  fontSize: '14px',
                  fontWeight: '700',
                  zIndex: 2,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                }}>
                  ✓
                </div>
              )}
              
              {/* Image */}
              <div style={{
                width: '100%',
                height: '70px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <img 
                  src={cuisine.image}
                  alt={cuisine.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: formData.cuisine.includes(cuisine.name) ? 'brightness(1.05)' : 'brightness(0.95)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div style={{
                  display: 'none',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}>
                  {cuisine.emoji}
                </div>
                {/* Gradient overlay */}
                {formData.cuisine.includes(cuisine.name) && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '30px',
                    background: 'linear-gradient(to top, rgba(212,175,55,0.3), transparent)'
                  }} />
                )}
              </div>
              
              {/* Text */}
              <div style={{
                padding: '10px 6px',
                background: formData.cuisine.includes(cuisine.name) 
                  ? 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)' 
                  : '#FFFFFF'
              }}>
                <span style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: formData.cuisine.includes(cuisine.name) ? '#92700C' : '#374151',
                  display: 'block'
                }}>
                  {cuisine.name}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Price Range */}
      <div>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '600', 
          color: '#374151', 
          marginBottom: '12px' 
        }}>
          Price Range
        </label>
        <div style={{ display: 'flex', gap: '12px' }}>
          {[
            { value: '₹', label: 'Budget', desc: 'Under ₹150' },
            { value: '₹₹', label: 'Moderate', desc: '₹150-300' },
            { value: '₹₹₹', label: 'Premium', desc: 'Above ₹300' }
          ].map(price => (
            <button
              key={price.value}
              type="button"
              onClick={() => updateField('price_range', price.value)}
              style={{
                flex: 1,
                padding: '16px 12px',
                borderRadius: '14px',
                border: formData.price_range === price.value ? '2px solid #D4AF37' : '2px solid #E5E7EB',
                background: formData.price_range === price.value 
                  ? 'rgba(212, 175, 55, 0.1)' 
                  : '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center'
              }}
            >
              <div style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: formData.price_range === price.value ? '#D4AF37' : '#1A1A1A',
                marginBottom: '4px'
              }}>
                {price.value}
              </div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{price.label}</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{price.desc}</div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Veg/Non-Veg Toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '16px',
        background: formData.pure_veg ? 'rgba(34, 197, 94, 0.1)' : '#FAFAFA',
        borderRadius: '14px',
        border: formData.pure_veg ? '2px solid #22C55E' : '2px solid #E5E7EB',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onClick={() => updateField('pure_veg', !formData.pure_veg)}
      >
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: formData.pure_veg ? '#22C55E' : '#E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          transition: 'all 0.2s ease'
        }}>
          🥬
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', color: '#1A1A1A', fontSize: '15px' }}>Pure Vegetarian</div>
          <div style={{ fontSize: '13px', color: '#6B7280' }}>Only veg food served</div>
        </div>
        <div style={{
          width: '52px',
          height: '28px',
          borderRadius: '14px',
          background: formData.pure_veg ? '#22C55E' : '#D1D5DB',
          position: 'relative',
          transition: 'all 0.2s ease'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '12px',
            background: '#FFFFFF',
            position: 'absolute',
            top: '2px',
            left: formData.pure_veg ? '26px' : '2px',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }} />
        </div>
      </div>
      
      {/* 24x7 Toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '16px',
        background: formData.open_24x7 ? 'rgba(212, 175, 55, 0.1)' : '#FAFAFA',
        borderRadius: '14px',
        border: formData.open_24x7 ? '2px solid #D4AF37' : '2px solid #E5E7EB',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onClick={() => updateField('open_24x7', !formData.open_24x7)}
      >
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: formData.open_24x7 ? '#D4AF37' : '#E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          transition: 'all 0.2s ease'
        }}>
          🕐
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', color: '#1A1A1A', fontSize: '15px' }}>Open 24×7</div>
          <div style={{ fontSize: '13px', color: '#6B7280' }}>Round the clock service</div>
        </div>
        <div style={{
          width: '52px',
          height: '28px',
          borderRadius: '14px',
          background: formData.open_24x7 ? '#D4AF37' : '#D1D5DB',
          position: 'relative',
          transition: 'all 0.2s ease'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '12px',
            background: '#FFFFFF',
            position: 'absolute',
            top: '2px',
            left: formData.open_24x7 ? '26px' : '2px',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }} />
        </div>
      </div>
      
      {/* Operating Hours */}
      {!formData.open_24x7 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '12px',
          padding: '16px',
          background: '#FAFAFA',
          borderRadius: '14px',
          border: '1px solid #E5E7EB'
        }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              ☀️ Opening Time
            </label>
            <input
              type="time"
              value={formData.open_time}
              onChange={(e) => updateField('open_time', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '2px solid #E5E7EB',
                borderRadius: '10px',
                outline: 'none',
                fontSize: '15px',
                background: '#FFFFFF'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              🌙 Closing Time
            </label>
            <input
              type="time"
              value={formData.close_time}
              onChange={(e) => updateField('close_time', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '2px solid #E5E7EB',
                borderRadius: '10px',
                outline: 'none',
                fontSize: '15px',
                background: '#FFFFFF'
              }}
            />
          </div>
        </div>
      )}
      
      {/* Special Tags */}
      <div>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '600', 
          color: '#374151', 
          marginBottom: '12px' 
        }}>
          Special Tags <span style={{ fontWeight: '400', color: '#9CA3AF' }}>(Optional - makes your dhaba stand out)</span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {TAG_OPTIONS.map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleArrayItem('tags', tag)}
              style={{
                padding: '12px 18px',
                borderRadius: '25px',
                fontSize: '13px',
                fontWeight: '600',
                border: formData.tags.includes(tag) ? 'none' : '2px solid #E5E7EB',
                background: formData.tags.includes(tag) 
                  ? 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 50%, #D4AF37 100%)' 
                  : '#FFFFFF',
                color: formData.tags.includes(tag) ? '#FFFFFF' : '#4B5563',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: formData.tags.includes(tag) ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'
              }}
            >
              {formData.tags.includes(tag) && '⭐ '}{tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  
  const renderStep4 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1A1A1A', marginBottom: '4px' }}>Amenities</h2>
        <p style={{ color: '#6B7280', fontSize: '14px' }}>Select available facilities at your dhaba</p>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[
          { key: 'parking', label: 'Parking Available', icon: '🅿️' },
          { key: 'washroom', label: 'Washroom', icon: '🚻' },
          { key: 'ac_dining', label: 'AC Dining', icon: '❄️' },
          { key: 'wifi', label: 'Free WiFi', icon: '📶' },
          { key: 'card_accepted', label: 'Card Payment Accepted', icon: '💳' },
          { key: 'family_friendly', label: 'Family Friendly', icon: '👨‍👩‍👧‍👦' },
          { key: 'play_area', label: 'Kids Play Area', icon: '🎠' },
          { key: 'ev_charging', label: 'EV Charging', icon: '⚡' },
        ].map(amenity => (
          <div 
            key={amenity.key} 
            onClick={() => updateField(amenity.key, !formData[amenity.key])}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '14px 16px',
              background: formData[amenity.key] ? 'rgba(26, 26, 26, 0.05)' : '#FAFAFA',
              borderRadius: '14px',
              border: formData[amenity.key] ? '2px solid #1A1A1A' : '2px solid #E5E7EB',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {/* Custom Checkbox - Black with Gold Tick */}
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: formData[amenity.key] ? '#1A1A1A' : '#FFFFFF',
              border: formData[amenity.key] ? '2px solid #1A1A1A' : '2px solid #D1D5DB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              flexShrink: 0
            }}>
              {formData[amenity.key] && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path 
                    d="M2 7L5.5 10.5L12 4" 
                    stroke="#D4AF37" 
                    strokeWidth="2.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            
            <span style={{ fontSize: '22px' }}>{amenity.icon}</span>
            <span style={{ 
              fontWeight: '500', 
              color: formData[amenity.key] ? '#1A1A1A' : '#4B5563',
              fontSize: '15px'
            }}>
              {amenity.label}
            </span>
          </div>
        ))}
      </div>
      
      {/* Parking Type - Multi-select Black Gold Gradient */}
      {formData.parking && (
        <div style={{
          padding: '16px',
          background: '#F9FAFB',
          borderRadius: '14px',
          border: '1px solid #E5E7EB'
        }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '12px' 
          }}>
            🚗 Parking Type <span style={{ fontWeight: '400', color: '#9CA3AF' }}>(Select all that apply)</span>
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {[
              { value: 'car', label: 'Car', icon: '🚗' },
              { value: 'truck', label: 'Truck', icon: '🚛' },
              { value: 'bus', label: 'Bus', icon: '🚌' },
              { value: 'all', label: 'All', icon: '🅿️' }
            ].map(type => {
              const isSelected = Array.isArray(formData.parking_type) 
                ? formData.parking_type.includes(type.value)
                : formData.parking_type === type.value;
              
              const handleParkingClick = () => {
                if (type.value === 'all') {
                  // If "All" is clicked, select all or deselect all
                  if (isSelected) {
                    updateField('parking_type', []);
                  } else {
                    updateField('parking_type', ['car', 'truck', 'bus', 'all']);
                  }
                } else {
                  // Toggle individual option
                  const currentTypes = Array.isArray(formData.parking_type) ? formData.parking_type : [];
                  let newTypes;
                  if (currentTypes.includes(type.value)) {
                    newTypes = currentTypes.filter(t => t !== type.value && t !== 'all');
                  } else {
                    newTypes = [...currentTypes.filter(t => t !== 'all'), type.value];
                    // If all individual options are selected, add 'all' too
                    if (newTypes.includes('car') && newTypes.includes('truck') && newTypes.includes('bus')) {
                      newTypes.push('all');
                    }
                  }
                  updateField('parking_type', newTypes);
                }
              };
              
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={handleParkingClick}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '25px',
                    fontSize: '14px',
                    fontWeight: '600',
                    border: isSelected ? 'none' : '2px solid #E5E7EB',
                    background: isSelected 
                      ? 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 50%, #D4AF37 100%)' 
                      : '#FFFFFF',
                    color: isSelected ? '#FFFFFF' : '#4B5563',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isSelected ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{type.icon}</span>
                  {type.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
  
  const renderStep5 = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Owner Details</h2>
      <p className="text-gray-500 text-sm">These details will be used for verification</p>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name *</label>
        <input
          type="text"
          value={formData.owner_name}
          onChange={(e) => updateField('owner_name', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="Full name of owner"
          data-testid="owner-name-input"
          style={fieldErrors.owner_name ? { borderColor: '#EF4444' } : {}}
        />
        <FieldError field="owner_name" />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Owner Phone *</label>
        <div className="flex items-center border rounded-xl overflow-hidden" style={fieldErrors.owner_phone ? { borderColor: '#EF4444' } : {}}>
          <span className="px-4 py-3 border-r border-gray-200 bg-gray-50">+91</span>
          <input
            type="tel"
            value={formData.owner_phone}
            onChange={(e) => updateField('owner_phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
            className="flex-1 px-4 py-3 outline-none"
            placeholder="Phone number"
            data-testid="owner-phone-input"
          />
        </div>
        <FieldError field="owner_phone" />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Owner Email</label>
        <input
          type="email"
          value={formData.owner_email}
          onChange={(e) => updateField('owner_email', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="email@example.com"
          style={fieldErrors.owner_email ? { borderColor: '#EF4444' } : {}}
        />
        <FieldError field="owner_email" />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Registered Business Address</label>
        <textarea
          value={formData.registered_address}
          onChange={(e) => updateField('registered_address', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="Registered address for business"
          rows={2}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">GSTN (GST Number)</label>
        <input
          type="text"
          value={formData.gstn}
          onChange={(e) => updateField('gstn', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15))}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="22AAAAA0000A1Z5"
          style={fieldErrors.gstn ? { borderColor: '#EF4444' } : {}}
        />
        <FieldError field="gstn" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">FSSAI License Number</label>
        <input
          type="text"
          value={formData.fssai_number}
          onChange={(e) => updateField('fssai_number', e.target.value.replace(/\D/g, '').slice(0, 14))}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-orange-500"
          placeholder="14-digit FSSAI number"
          data-testid="fssai-input"
          style={fieldErrors.fssai_number ? { borderColor: '#EF4444' } : {}}
        />
        <FieldError field="fssai_number" />
        <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>Required on tax invoices for food businesses</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone (for customers)</label>
        <div className="flex items-center border rounded-xl overflow-hidden" style={fieldErrors.contact_phone ? { borderColor: '#EF4444' } : {}}>
          <span className="px-4 py-3 border-r border-gray-200 bg-gray-50">+91</span>
          <input
            type="tel"
            value={formData.contact_phone}
            onChange={(e) => updateField('contact_phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
            className="flex-1 px-4 py-3 outline-none"
            placeholder="Customer contact number"
          />
        </div>
        <FieldError field="contact_phone" />
      </div>
    </div>
  );
  
  const renderStep6 = () => {
    const handleCategoryUpload = (categoryId, e) => {
      const file = e.target.files[0];
      if (!file || !file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          photos: { ...prev.photos, [categoryId]: [...(prev.photos[categoryId] || []), event.target.result] }
        }));
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    };

    const removeCategoryPhoto = (categoryId, idx) => {
      setFormData(prev => ({
        ...prev,
        photos: { ...prev.photos, [categoryId]: (prev.photos[categoryId] || []).filter((_, i) => i !== idx) }
      }));
    };

    const totalPhotos = PHOTO_CATEGORIES.reduce((sum, cat) => sum + (formData.photos[cat.id]?.length || 0), 0);
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1A1A1A', marginBottom: '4px' }}>Photos</h2>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>Upload photos by category to help customers and auditors</p>
        </div>

        {PHOTO_CATEGORIES.map(cat => {
          const photos = formData.photos[cat.id] || [];
          return (
            <div key={cat.id} style={{ background: '#FFF', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '14px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>
                    {cat.label} {cat.required && <span style={{ color: '#EF4444' }}>*</span>}
                  </span>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{cat.helper}</p>
                </div>
                <span style={{ fontSize: '12px', color: photos.length > 0 ? '#22C55E' : '#9CA3AF', fontWeight: '600' }}>
                  {photos.length} photo{photos.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {photos.map((url, idx) => (
                  <div key={`${cat.id}-${idx}`} style={{ position: 'relative', width: '70px', height: '70px' }}>
                    <img src={url} alt="" style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                    <button onClick={() => removeCategoryPhoto(cat.id, idx)} type="button"
                      style={{ position: 'absolute', top: '-5px', right: '-5px', width: '20px', height: '20px', borderRadius: '50%', background: '#EF4444', color: '#FFF', border: '2px solid #FFF', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      ✕
                    </button>
                  </div>
                ))}
                <label style={{ width: '70px', height: '70px', borderRadius: '8px', border: '2px dashed #D4AF37', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'rgba(212,175,55,0.05)' }}>
                  <span style={{ fontSize: '18px', color: '#D4AF37' }}>+</span>
                  <span style={{ fontSize: '9px', color: '#D4AF37' }}>Add</span>
                  <input type="file" style={{ display: 'none' }}
                    onChange={(e) => handleCategoryUpload(cat.id, e)} />
                </label>
              </div>
            </div>
          );
        })}

        <p style={{ fontSize: '12px', color: '#6B7280', textAlign: 'center' }}>
          Total: {totalPhotos} photo{totalPhotos !== 1 ? 's' : ''} uploaded
        </p>

        {/* Registration Summary */}
        <div style={{
          padding: '16px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '14px'
        }}>
          <h3 style={{ fontWeight: '600', color: '#059669', marginBottom: '12px', fontSize: '15px' }}>
            ✅ Registration Summary
          </h3>
          <div style={{ fontSize: '13px', color: '#065F46', lineHeight: '1.8' }}>
            <p><strong>Dhaba:</strong> {formData.name || '-'}</p>
            <p><strong>Highway:</strong> {formData.highway || '-'}</p>
            <p><strong>Cuisine:</strong> {formData.cuisine.length > 0 ? formData.cuisine.join(', ') : '-'}</p>
            <p><strong>Owner:</strong> {formData.owner_name || '-'}</p>
            <p><strong>Photos:</strong> {PHOTO_CATEGORIES.reduce((sum, cat) => sum + (formData.photos[cat.id]?.length || 0), 0)} uploaded</p>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-light">
      {/* Premium Header */}
      <div 
        style={{
          background: 'linear-gradient(180deg, #121212 0%, #1E1E1E 100%)',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
          position: 'sticky',
          top: 0,
          zIndex: 20
        }}
      >
        <button 
          onClick={onBack}
          style={{ 
            background: 'rgba(212, 175, 55, 0.1)', 
            border: '1px solid rgba(212, 175, 55, 0.2)', 
            cursor: 'pointer', 
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#D4AF37',
            fontSize: '18px'
          }}
        >
          ←
        </button>
        <h1 
          style={{ 
            fontSize: '18px', 
            fontWeight: '700',
            fontFamily: "'Manrope', sans-serif",
            color: '#D4AF37'
          }}
        >
          Register Your Dhaba
        </h1>
      </div>
      
      <div className="p-4">
        {renderStepIndicator()}
        
        {error && (
          <div 
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              color: '#EF4444',
              fontSize: '14px'
            }}
          >
            {error}
          </div>
        )}
        
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
        {step === 6 && renderStep6()}
        
        {/* Navigation Buttons - Premium Styling */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              style={{
                flex: 1,
                padding: '16px',
                border: '2px solid #D4AF37',
                borderRadius: '9999px',
                fontWeight: '600',
                background: 'transparent',
                color: '#D4AF37',
                cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                transition: 'all 0.2s ease'
              }}
              data-testid="back-btn"
            >
              Back
            </button>
          )}
          
          {step < 6 ? (
            <button
              onClick={handleNext}
              style={{
                flex: 1,
                padding: '16px',
                border: 'none',
                borderRadius: '9999px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #D4AF37, #C5A028)',
                color: '#121212',
                cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
                transition: 'all 0.2s ease'
              }}
              data-testid="next-btn"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || !canProceed()}
              style={{
                flex: 1,
                padding: '16px',
                border: 'none',
                borderRadius: '9999px',
                fontWeight: '600',
                background: (!loading && canProceed()) 
                  ? 'linear-gradient(135deg, #10B981, #059669)' 
                  : '#E5E7EB',
                color: (!loading && canProceed()) ? '#FFFFFF' : '#9CA3AF',
                cursor: (!loading && canProceed()) ? 'pointer' : 'not-allowed',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                boxShadow: (!loading && canProceed()) ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none',
                transition: 'all 0.2s ease'
              }}
              data-testid="submit-btn"
            >
              {loading ? 'Submitting...' : 'Submit Registration'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DhabaRegistrationForm;
